import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PhoneNumberUtil } from 'google-libphonenumber';
import { BlobServiceClient } from "@azure/storage-blob";
import { useUserContext } from "../../../context/userContext";
import { useHeaderContext } from "../../../context/headerContext";
import { useDrawerContext } from "../../../context/drawerContext";
import { days, months, years } from "../../../utils/calendar"
import { Link } from "react-router-dom";
import Button from "../../../components/Button";
import DropDown from "../../../components/Dropdown";
import CheckBox from "../../../components/CheckBox";
import VoiceNote from "../../../components/VoiceNote";
import TextInput from "../../../components/TextInput";
import AdvancedPhoneInput from "../../../components/AdvancedPhoneInput";
import AzureProfilePictureUpload from "../../../components/AzureProfilePictureUpload";
import SelectInput from "../../../components/SelectInput";
import TextArea from "../../../components/TextArea";
import EditPencilIcon from "/public/images/icons/editPencil.svg?react";
import AddIcon from "/public/images/icons/add.svg?react";
import CloseIcon from "/public/images/icons/close.svg?react";
import BinIcon from "/public/images/icons/trashRed.svg?react";
import EmailIcon from "/public/images/icons/email.svg?react";
import PhoneIcon from "/public/images/icons/phone.svg?react";
import Toggle from "../../../components/Toggle";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { useLiteDrawer } from '../../../hooks/useLiteDrawer';
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";
const phoneUtil = PhoneNumberUtil.getInstance();

export default function ParticipantEditProfile() {
  const { state  } = useLocation();
  const { participant, band, medicalConditions, allergies, medications, otherEmergencyContacts } = state;
  const { setTitle, setBackButton } = useHeaderContext();
  const { user, logout, updateTokens } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { showLiteDrawer } = useLiteDrawer();
  const navigate = useNavigate();
  const [updatedParticipant, setUpdatedParticipant] = useState(participant);
  const [medicalHistory, setMedicalHistory] = useState(medicalConditions || []);
  const [removeMedicalHistories, setRemoveMedicalHistories] = useState([]);
  const [updatedAllergies, setUpdatedAllergies] = useState(allergies);
  const [removeAllergies, setRemoveAllergies] = useState([]);
  const [updatedMedications, setUpdatedMedications] = useState(medications);
  const [removeMedications, setRemoveMedications] = useState([]);
  const [updatedEmergencyContacts, setUpdatedEmergencyContacts] = useState(otherEmergencyContacts);
  const [removeEmergencyContacts, setRemoveEmergencyContacts] = useState([]);
  const [phoneNumberErrors, setPhoneNumberErrors] = useState([]);
  const [editingContactSMS, setEditingContactSMS] = useState([]);
  const [participantNameWarning, setParticipantNameWarning] = useState(false);
  const [primaryICEWarning, setPrimaryICEWarning] = useState(false);
  const [participantPrimaryWarning, setParticipantPrimaryWarning] = useState(false);
  const [hasAcknowledgedCoreWarning, setHasAcknowledgedCoreWarning] = useState(false);
  const [selectedDay, setSelectedDay] = useState(updatedParticipant?.DateOfBirth?.split("-")[2] || '');
  const [selectedMonth, setSelectedMonth] = useState(updatedParticipant?.DateOfBirth?.split("-")[1] || '');
  const [selectedYear, setSelectedYear] = useState(updatedParticipant?.DateOfBirth?.split("-")[0] || '');
  const [participantDOB, setParticipantDOB] = useState(updatedParticipant?.DateOfBirth || '');
  const [updatingErrors, setUpdatingErrors] = useState([]);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [bandData, setBandData] = useState(null);

  useEffect(() => {
    if (selectedDay && selectedMonth && selectedYear) {
      setParticipantDOB(`${selectedYear}-${selectedMonth}-${selectedDay}`);
    }
  }, [selectedDay, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchBand();
    setTitle(`Editing ${updatedParticipant?.ParticipantName}'s PROFILE`);
    setBackButton(true)
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const base64ToFile = (base64Data, fileName, contentType) => {
    const byteCharacters = atob(base64Data.split(',')[1]); // Extract the Base64 part after the comma
    const byteArrays = [];
  
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
  
    const byteArray = new Uint8Array(byteArrays);
    const blob = new Blob([byteArray], { type: contentType });
  
    // Create a File object from the Blob, with the original file name
    return new File([blob], fileName, { type: contentType });
  };

  const handleBandActivation = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    const formDetails = new FormData(e.currentTarget);
    let allSuccessful = true;

    if(!formDetails.get('participantDOB')){
      dobNotSet();
      return;
    }
  
    let audioBlob = null;

    const returnAudioBlob = async () => {
      if(formDetails.get('uploadedVoiceNote')){
      if (!formDetails.get('uploadedVoiceNote').includes('blob.core.windows.net')) {
        const res = await fetch(formDetails.get("uploadedVoiceNote"));
        const blob = await res.blob();
        audioBlob = blob;
      }}
    };
    
    let audioURL = formDetails.get('uploadedVoiceNote');

    const handleAudioFileUpload = async () => {
      const account = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT;
      const sasKey = import.meta.env.VITE_AZURE_VOICE_NOTE_STORAGE_SAS_TOKEN;
      const container = import.meta.env.VITE_AZURE_VOICE_NOTE_STORAGE;

      try {
          const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?sv=${sasKey}`);
          const containerClient = blobServiceClient.getContainerClient(container);
          const blobName =  updatedParticipant?.ParticipantId + '-voice-note.mp3';
          const blockBlobClient = containerClient.getBlockBlobClient(blobName);
          const arrayBuffer = await audioBlob.arrayBuffer();
          const uploadBlobResponse = await blockBlobClient.upload(arrayBuffer, arrayBuffer.byteLength);

          if (uploadBlobResponse._response.status === 201) {
              const uploadedBlobUrl = `https://${account}.blob.core.windows.net/${container}/${blobName}`;
              audioURL = uploadedBlobUrl;
              LogErrors(user, logout, updateTokens, audioURL);
          } else {
              LogErrors(user, logout, updateTokens, "Upload failed. Status code: " + uploadBlobResponse._response.status);
          }
      } catch(error) {
          LogErrors(user, logout, updateTokens, "Error uploading file: " + error);
      }
    }

    let profilePictureFile = null;
    let profilePictureURL = formDetails.get('uploadedProfilePicture');

    const returnProfilePictureFile = () => {
      if(profilePictureURL){
        if (!formDetails.get('uploadedProfilePicture').includes('blob.core.windows.net')) {
          const base64String = formDetails.get("uploadedProfilePicture"); // Retrieve Base64 string
          // console.log(base64String);
          const fileName = formDetails.get("uploadedProfilePictureName") || `${updatedParticipant?.ParticipantId}-profile-picture.png`; // Retrieve file name
        
          if (base64String && fileName) {
            const contentType = base64String.split(',')[0].split(':')[1].split(';')[0]; // Extract MIME type
            profilePictureFile = base64ToFile(base64String, fileName, contentType); // Convert Base64 to File object
          }
        }
      }
    };

    const handleProfilePictureUpload = async () => {
      const account = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT;
      const sasKey = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE_SAS_TOKEN;
      const container = import.meta.env.VITE_AZURE_PROFILE_PICTURE_STORAGE;

      try {
          const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net?sv=${sasKey}`);
          const containerClient = blobServiceClient.getContainerClient(container);
          const lastDotIndex = profilePictureFile.name.lastIndexOf('.');
          const fileExtension = lastDotIndex > 0 ? profilePictureFile.name.substring(lastDotIndex + 1) : ''; 

          const blobName = updatedParticipant?.ParticipantId + "-profile-picture." + fileExtension;
          const blockBlobClient = containerClient.getBlockBlobClient(blobName);
          const arrayBuffer = await profilePictureFile.arrayBuffer();
          const uploadBlobResponse = await blockBlobClient.upload(arrayBuffer, arrayBuffer.byteLength);

          if (uploadBlobResponse._response.status === 201) {
              const uploadedBlobUrl = `https://${account}.blob.core.windows.net/${container}/${blobName}`;
              profilePictureURL = uploadedBlobUrl;
              // ImageCDNUploaded = true;
          } else {
              LogErrors(user, logout, updateTokens, "Upload failed. Status code: " + uploadBlobResponse._response.status);
              // ImageCDNUploaded = false;
          }
      } catch(error) {
          LogErrors(user, logout, updateTokens, "Error uploading file: " + error);
          // ImageCDNUploaded = false;
      }
    }

    // Save User Profile
    const saveUserProfile = async () => {
      let phoneErrors = [];
      let success = true;
      
      await Sentry.startSpan({ name: "Save User Profile", op: "http.request" }, async (span) => {
        try {
          const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(formDetails.get("bandICEPhoneNumber"));
          const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);
    
          if (!validPhoneNumber) {
            phoneErrors.push({ name: `bandICEPhoneNumber`, error: 'This is not a valid phone number.' });
          }
        } catch {
          phoneErrors.push({ name: `bandICEPhoneNumber`, error: 'This is not a valid phone number.' });
        }
    
        // If there are any errors, set them in the state
        if (phoneErrors.length > 0) {
          setUpdatingErrors(phoneErrors);
          span.setStatus("error");
          span.setAttribute("validation", "failed");
          success = false;
          return;
        }
    
        if (formDetails.get('voiceNoteRemoved') === "true") {
          audioURL = "";
        }
    
        try {
          const url = import.meta.env.VITE_API_URL + "Profile/SaveUserProfile";
          span.setAttribute("http.url", url);
    
          const saveUserResponse = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              participantId: updatedParticipant?.ParticipantId,
              participantName: formDetails.get("participantName"),
              participantDateOfBirth: formDetails.get("participantDOB"),
              primaryICEName: formDetails.get("bandICEName"),
              primaryICENumber: formDetails.get("bandICEPhoneNumber"),
              primaryICERelationship: formDetails.get("bandICERelation"),
              PrimaryMedicalCondition: formDetails.get("bandPrimaryCondition"),
              EmergencySMSPrimaryContact: formDetails.get("EmergencySMSPrimaryContact") === "on" || formDetails.get("EmergencySMSPrimaryContact") === "1" ? 1 : 0,
              notes: formDetails.get("participantNotes"),
              audioURL: audioURL ? audioURL : "",
              profilePicture: profilePictureURL,
            }),
          }, logout, updateTokens, (message) => { toast.info(message); });
    
          span.setAttribute("http.status_code", saveUserResponse.status);
    
          if (!saveUserResponse.ok) {
            Sentry.captureMessage("Failed to save participant's profile", { extra: { status: saveUserResponse.status, url } });
            LogErrors(user, logout, updateTokens, "Failed to save participant's profile.");
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error saving participant's profile: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });

      return success;
    };
  
    // Save Additional ICE Contacts
    const deleteAllAdditionalICEContacts = async () => {
      await Sentry.startSpan({ name: "Delete Additional ICE Contacts", op: "http.request" }, async (span) => {
        try {
          const promises = removeEmergencyContacts.map((contact) => {
            const url = import.meta.env.VITE_API_URL + `Profile/DeleteAdditionalICEContact?additionalicecontactid=${contact?.AdditionalICEContactId}`;
            span.setAttribute("http.url", url);
    
            return fetchWithRefresh(url, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${contact?.AdditionalICEContactId}`, response.status);
              return { AdditionalICEContactId: contact.AdditionalICEContactId, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { AdditionalICEContactId: contact.AdditionalICEContactId, url } });
              return { AdditionalICEContactId: contact.AdditionalICEContactId, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete contact: ${result?.AdditionalICEContactId}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to delete contact: ${result?.AdditionalICEContactId}`]);
              Sentry.captureMessage("Failed to delete ICE contact", {
                extra: { AdditionalICEContactId: result?.AdditionalICEContactId, error: result?.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error deleting ICE contacts: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const saveAllAdditionalICEContacts = async () => {
      await Sentry.startSpan({ name: "Save Additional ICE Contacts", op: "http.request" }, async (span) => {
        try {
          const promises = updatedEmergencyContacts.map((contact) => {
            const url = import.meta.env.VITE_API_URL + "Profile/SaveAdditionalICEContact";
            span.setAttribute("http.url", url);
    
            return fetchWithRefresh(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: updatedParticipant?.ParticipantId,
                additionalICEName: contact.name || contact?.AdditionalICEName,
                additionalICENumber: contact.phoneNumber || contact?.AdditionalICENumber,
                additionalICERelationship: contact.relation || contact?.AdditionalICERelationship,
                emergencySMS: contact.sms ? contact.sms === "on" || contact.sms === "1" ? 1 : 0 : contact?.EmergencySMS ? 1 : 0,
                additionalICEContactId: contact?.AdditionalICEContactId,
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${contact.name}`, response.status);
              return { contactName: contact.name, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { contactName: contact.name, url } });
              return { contactName: contact.name, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save ICE contact: ${result?.contactName}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to save ICE contact: ${result?.contactName}`]);
              Sentry.captureMessage("Failed to save ICE contact", {
                extra: { contactName: result?.contactName, error: result?.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error saving ICE contacts: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };
  
    // Save Medical History
    const deleteAllMedicalHistory = async () => {
      await Sentry.startSpan({ name: "Delete Medical History", op: "http.request" }, async (span) => {
        try {
          const promises = removeMedicalHistories.map((history) => {
            const url = import.meta.env.VITE_API_URL + `Profile/DeleteMedicalHistoryItem?medicalhistoryid=${history?.MedicalHistoryId}`;
            span.setAttribute("http.url", url);
    
            return fetchWithRefresh(url, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${history.MedicalHistoryId}`, response.status);
              return { MedicalHistoryId: history.MedicalHistoryId, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { MedicalHistoryId: history.MedicalHistoryId, url } });
              return { MedicalHistoryId: history.MedicalHistoryId, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete medical history: ${result.MedicalHistoryId}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to delete medical history: ${result.MedicalHistoryId}`]);
              Sentry.captureMessage("Failed to delete medical history", {
                extra: { MedicalHistoryId: result?.MedicalHistoryId, error: result?.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error deleting medical history: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const saveAllMedicalHistory = async () => {
      await Sentry.startSpan({ name: "Save Medical History", op: "http.request" }, async (span) => {
        try {
          const promises = medicalHistory.map((history) => {
            const url = import.meta.env.VITE_API_URL + "Profile/SaveMedicalHistoryItem";
            span.setAttribute("http.url", url);
            span.setAttribute("medicalHistoryId", history?.MedicalHistoryId);
    
            return fetchWithRefresh(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: updatedParticipant.ParticipantId,
                conditionName: history?.ConditionName,
                notes: history?.Notes || '',
                ongoing: history?.OnGoing,
                medicalHistoryId: history?.MedicalHistoryId,
                dateAdded: history?.DateAdded.split('T')[0],
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${history.MedicalHistoryId}`, response.status);
              return { historyName: history.ConditionName, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { historyName: history.ConditionName, medicalHistoryId: history.MedicalHistoryId, url } });
              return { historyName: history.ConditionName, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save medical history: ${result.historyName}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to save medical history: ${result.historyName}`]);
              Sentry.captureMessage("Failed to save medical history", {
                extra: { historyName: result.historyName, error: result.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error saving medical history: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };
  
    // Save Allergies
    const deleteAllAllergies = async () => {
      await Sentry.startSpan({ name: "Delete All Allergies", op: "http.request" }, async (span) => {
        try {
          const promises = removeAllergies.map((allergy) => {
            const url = import.meta.env.VITE_API_URL + `Profile/DeleteAllergy?allergyid=${allergy?.AllergyId}`;
            span.setAttribute("http.url", url);
            span.setAttribute("allergyId", allergy?.AllergyId);
    
            return fetchWithRefresh(url, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${allergy.AllergyId}`, response.status);
              return { AllergyId: allergy.AllergyId, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { AllergyId: allergy.AllergyId, url } });
              return { AllergyId: allergy.AllergyId, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete allergy: ${result.AllergyId}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to delete allergy: ${result.AllergyId}`]);
              Sentry.captureMessage("Failed to delete allergy", {
                extra: { AllergyId: result.AllergyId, error: result.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error deleting allergies: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const saveAllAllergies = async () => {
      await Sentry.startSpan({ name: "Save All Allergies", op: "http.request" }, async (span) => {
        try {
          const promises = updatedAllergies.map((allergy) => {
            const url = import.meta.env.VITE_API_URL + "Profile/SaveAllergy";
            span.setAttribute("http.url", url);
            span.setAttribute("allergyId", allergy?.AllergyId);
    
            return fetchWithRefresh(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: updatedParticipant.ParticipantId,
                conditionName: allergy?.name || allergy?.ConditionName,
                notes: allergy?.additionalInformation || allergy?.Notes || '',
                ongoing: allergy?.ongoing || allergy?.OnGoing,
                allergyId: allergy?.AllergyId,
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${allergy.AllergyId}`, response.status);
              return { allergyName: allergy.name, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { allergyId: allergy.AllergyId, url } });
              return { allergyName: allergy.name, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save allergy: ${result.allergyName}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to save allergy: ${result.allergyName}`]);
              Sentry.captureMessage("Failed to save allergy", {
                extra: { allergyName: result.allergyName, error: result.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error saving allergies: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };
  
    // Save & Delete Medications
    const deleteAllMedications = async () => {
      await Sentry.startSpan({ name: "Delete All Medications", op: "http.request" }, async (span) => {
        try {
          const promises = removeMedications.map((medication) => {
            const url = import.meta.env.VITE_API_URL + `Profile/DeleteMedication?medicationid=${medication?.MedicationId}`;
            span.setAttribute("http.url", url);
            span.setAttribute("medicationId", medication?.MedicationId);
    
            return fetchWithRefresh(url, {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${medication.MedicationId}`, response.status);
              return { medicationid: medication.MedicationId, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { medicationId: medication.MedicationId, url } });
              return { medicationid: medication.MedicationId, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to delete medication: ${result.medicationid}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to delete medication: ${result.medicationid}`]);
              Sentry.captureMessage("Failed to delete medication", {
                extra: { medicationId: result.medicationid, error: result.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error deleting medications: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const saveAllMedications = async () => {
      await Sentry.startSpan({ name: "Save All Medications", op: "http.request" }, async (span) => {
        try {
          const promises = updatedMedications.map((medication) => {
            const url = import.meta.env.VITE_API_URL + "Profile/SaveMedication";
            span.setAttribute("http.url", url);
            span.setAttribute("medicationId", medication?.MedicationId || "new");
    
            return fetchWithRefresh(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: updatedParticipant.ParticipantId,
                medicationName: medication?.medicationName || medication?.MedicationName,
                dosage: medication?.medicationDosage || medication?.Dosage,
                frequency: medication?.medicationFrequency || medication?.Frequency,
                ongoing: medication?.ongoing || medication?.OnGoing,
                notes: medication?.additionalInformation || medication?.Notes || "",
                medicationId: medication?.MedicationId || "",
              }),
            }, logout, updateTokens, (message) => { toast.info(message); })
            .then((response) => {
              span.setAttribute(`http.status_code_${medication.medicationName}`, response.status);
              return { medicationName: medication.medicationName, ok: response.ok };
            })
            .catch((error) => {
              Sentry.captureException(error, { extra: { medicationName: medication.medicationName, url } });
              return { medicationName: medication.medicationName, ok: false, error };
            });
          });
    
          const results = await Promise.all(promises);
    
          results.forEach((result) => {
            if (!result.ok) {
              LogErrors(user, logout, updateTokens, `Failed to save medication: ${result.medicationName}`);
              setUpdatingErrors((prevErrors) => [...prevErrors, `Failed to save medication: ${result.medicationName}`]);
              Sentry.captureMessage("Failed to save medication", {
                extra: { medicationName: result.medicationName, error: result.error },
              });
              allSuccessful = false;
            }
          });
    
          span.setStatus("ok");
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error saving medications: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };
  
    // // Execute all API calls
    await returnAudioBlob();
    await returnProfilePictureFile();
    
    if(audioBlob){
      await handleAudioFileUpload();
    }

    if(profilePictureFile){
      await handleProfilePictureUpload();
    }

    const profileSaved = await saveUserProfile();

    if (!profileSaved) {
      setUpdatingProfile(false);
      return;
    }

    if(removeEmergencyContacts.length > 0){
      await deleteAllAdditionalICEContacts();
    }

    if(updatedEmergencyContacts.length > 0){
      await saveAllAdditionalICEContacts();
    }

    if(removeMedicalHistories.length > 0){
      await deleteAllMedicalHistory();
    }

    if(medicalHistory.length > 0){
      await saveAllMedicalHistory();
    }

    if(removeAllergies.length > 0){
      await deleteAllAllergies();
    }

    if(updatedAllergies.length > 0){
      await saveAllAllergies();
    }

    if(removeMedications.length > 0){
      await deleteAllMedications();
    }

    if(updatedMedications.length > 0){
      await saveAllMedications();
    }

    if(allSuccessful){
      setUpdatingProfile(false);
      navigate("/");
      openProfileDrawer(allSuccessful);
    } else {
      setUpdatingProfile(false);
      openProfileDrawer(allSuccessful);
    }
  };

  const dobNotSet = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">DATE OF BIRTH</h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false);}} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">No date of birth</h3>
          <p className="text-sportsDarkGrey mb-[26px]">A date of birth is required for all participants to ensure detailed and accurate information can be provided in the case of an emergency.</p>
          <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
        </div>
      </div>
    );
  }

  const openProfileDrawer = (success) => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            {success ? "UPDATED" : "ERROR"}
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch gap-6">
          <h3 className="text-[24px]">
            {updatedParticipant?.ParticipantName}&apos;s Profile {success ? "Successfully Updated" : "Failed to Update"}
          </h3>
          
          {success ? (
            <p className="text-sportsDarkGrey">
              Your updates can now be viewed on the updated participant&apos;s profile.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <p className=" text-sportsDarkGrey">If this issue is still persisting, please don't hesitate to get in touch</p>
              <p className=" text-sportsDarkGrey mb-4">
                We&apos;re happy to assist with any query. Our offices are open
                Monday - Friday between 09:00 - 16:30.
              </p>
              <div className="flex flex-col gap-4">
                <Link className="flex items-center gap-2" to="tel:0800 999 3669">
                  <PhoneIcon />{" "}
                  <Button
                    status="nobg"
                    variant="tertiary"
                    onClick={() => setOpen(false)}
                  >
                    0800 999 3669
                  </Button>
                </Link>
                <Link
                  className="flex items-center gap-2"
                  to="mailto:hello@sportsguardian.com"
                >
                  <EmailIcon />{" "}
                  <Button
                    status="nobg"
                    variant="tertiary"
                    onClick={() => setOpen(false)}
                  >
                    hello@sportsguardian.com
                  </Button>{" "}
                </Link>
              </div>
            </div>
          )}

          <Button variant='primary' onClick={() => setOpen(false)} className="w-full">Close</Button>
        </div>
      </div>
    );
  }

  //START MEDICAL HISTORY LOGIC
  const openMedicalDrawer = (index) => {
    const isEditing = index < medicalHistory.length; // Check if it's an edit operation
    
    setOpen(true);
    setDrawerContent(
      <div className="w-full" key={`drawer-${index}`}>
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Medical History
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px]">
            {isEditing ? "Edit your medical condition" : "Enter your condition name"}
          </h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            You can update your medical history anytime from your profile page.
          </p>
          <form
            onSubmit={(e) => isEditing ? editMedicalHistory(e, index) : addMedicalHistory(e, index)}
            className="flex flex-col gap-6"
          >
            <TextInput
              defaultValue={isEditing ? medicalHistory[index]?.ConditionName || "" : ""}
              label={"Condition Name"}
              name={`conditionName${index}`}
            />

            <TextInput
              defaultValue={isEditing ? medicalHistory[index]?.Notes || "" : ""}
              label="Additional Information"
              name={`additionalInformation${index}`}
            />

            <label className={`flex flex-col gap-1`}>
              <div>
                <span className="text-sportsBlack font-medium text-base leading-[26px] mb-1">Ongoing</span>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  defaultChecked={isEditing ? medicalHistory[index]?.OnGoing === 1 ? true : false : false }
                  onChange={checkOngoingMedicalHistory}
                  className="checkbox checkbox-medical"
                  name={`ongoing${index}`}
                />
              </div>
            </label>

            <Button submit className="!text-[#101820]">
              Update Medical Condition
            </Button>

            {isEditing && (
              <Button
                onClick={() => removeMedicalHistory(index)}
                status="nobg"
                className="flex w-full justify-center gap-2 text-sportsRed underline font-semibold"
              >
                <BinIcon />
                Remove
              </Button>
            )}
          </form>
        </div>
      </div>
    );
  };

  const addMedicalHistory = (event, index) => {
      event.preventDefault();
      const newDetails = new FormData(event.currentTarget);
      const ongoing = newDetails.get(`ongoing${index}`);

          const conditionName = newDetails.get(`conditionName${index}`)?.trim();
          const additionalInformation = newDetails.get(`additionalInformation${index}`)?.trim();

     if (!conditionName) {
      return;
    }

      const newCondition = {
        ConditionName: conditionName,
        DateAdded: new Date().toISOString(),
        Notes: additionalInformation || "",
        OnGoing: ongoing === "on" ? 1 : 0,
      };

      setMedicalHistory([...medicalHistory, newCondition]);
      setOpen(false);
      setDrawerContent(<div></div>);
    };

    const editMedicalHistory = (event, index) => {
      event.preventDefault();
      
      const updatedDetails = new FormData(event.currentTarget);
      const newMedicalHistory = medicalHistory.map((item, i) => {
        if (i === index) {
          const ongoing = updatedDetails.get(`ongoing${index}`);

          return {
            ...item,
            ConditionName: updatedDetails.get(`conditionName${index}`),
            Notes: updatedDetails.get(`additionalInformation${index}`),
            OnGoing: ongoing === "on" ? 1 : 0,
          };
        }
        return item;
      });

      setMedicalHistory(newMedicalHistory);
      setOpen(false);
      setDrawerContent(<div></div>);
    };

    const removeMedicalHistory = (index) => {
      const historyToRemove = medicalHistory[index]; // Get the medical history entry being removed
    
      // Check if the entry has an ID and add it to the removeMedicalHistories state
      if (historyToRemove.MedicalHistoryId) {
        setRemoveMedicalHistories((prev) => [...prev, historyToRemove]);
      }
    
      const newMedicalHistory = medicalHistory.filter((_, i) => i !== index);
      setMedicalHistory(newMedicalHistory);
      setOpen(false);
    };    
    
    const checkOngoingMedicalHistory = (index) => {
      const newMedicalHistory = medicalHistory.map((item, i) => {
        if (i === index) {
          return { ...item, ongoing: !item.ongoing };
        }
        return item;
      });
    
      setMedicalHistory(newMedicalHistory);
    };
  //END MEDICAL HISTORY LOGIC

  //START ALLERGY LOGIC
  const openAllergyDrawer = (index) => {
    const isEditing = index < updatedAllergies.length; // Check if it's an edit operation
    
    setOpen(true);
    setDrawerContent(
      <div className="w-full" key={`drawer-${index}`}>
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          ALLERGIES
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px]">
            {isEditing ? "Edit your allergy" : "Enter your allergy name"}
          </h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            You can update your allergies anytime from your profile page.
          </p>
          <form
            onSubmit={(e) => isEditing ? editAllergies(e, index) : addAllergies(e, index)}
            className="flex flex-col gap-6"
          >
            <TextInput
              key={"allergyName"}
              label={"Allergy Name"}
              name={`allergyName${index}`}
              defaultValue={isEditing ? updatedAllergies[index]?.name || updatedAllergies[index]?.ConditionName || "" : "" }
            />
            <TextInput
              key={"additionalAllergy"}
              label="Additional Information"
              name={`additionalInformation${index}`}
              defaultValue={isEditing ? updatedAllergies[index]?.additionalInformation || updatedAllergies[index]?.Notes || "" : "" }
            />

              {/* <label className={`flex flex-col gap-1`}>
                <div>
                  <span className="text-sportsBlack font-medium text-base leading-[26px] mb-1">Ongoing</span>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    defaultChecked={isEditing ? updatedAllergies[index]?.ongoing === 1 || updatedAllergies[index]?.OnGoing === 1 ? true : false : false }
                    onChange={checkOngoingAllergies}
                    className="checkbox checkbox-medical"
                    name={`ongoing${index}`}
                  />
                </div>
              </label> */}

            <Button submit className="!text-[#101820]">
              Update Allergy
            </Button>

            {isEditing && (
              <Button
                onClick={() => removeAllergy(index)}
                status="nobg"
                className="flex w-full justify-center gap-2 text-sportsRed underline font-semibold"
              >
                <BinIcon />
                Remove
              </Button>
            )}
          </form>
        </div>
      </div>
    );
  };

  const addAllergies = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);
    const ongoing = newDetails.get(`ongoing${index}`) || "off";

     const allergyName = newDetails.get(`allergyName${index}`)?.trim();
    const additionalInformation = newDetails.get(`additionalInformation${index}`)?.trim();

    if (!allergyName) {
      return;
    }

    const newAllergyData = {
      name: allergyName,
      Notes: additionalInformation || "",
      dateAdded: new Date(),
      OnGoing: ongoing == "on" ? 1 : 0,
    };

    setUpdatedAllergies([...updatedAllergies, newAllergyData]);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const editAllergies = (event, index) => {
    event.preventDefault();
    const updatedDetails = new FormData(event.currentTarget);

    const newAllergies = updatedAllergies.map((item, i) => {
      if (i === index) {
        const ongoing = updatedDetails.get(`ongoing${index}`) || "off";

        return {
          ...item,
          name: updatedDetails.get(`allergyName${index}`),
          Notes: updatedDetails.get(`additionalInformation${index}`),
          OnGoing: ongoing == "on" ? 1 : 0,
        };
      }
      return item;
    });

    setUpdatedAllergies(newAllergies);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const removeAllergy = (index) => {
    const allergyToRemove = updatedAllergies[index]; // Get the allergy being removed
  
    // Check if the allergy has an ID and add it to the removeAllergies state
    if (allergyToRemove.AllergyId) {
      setRemoveAllergies((prev) => [...prev, allergyToRemove]);
    }
  
    const newAllergies = updatedAllergies.filter((_, i) => i !== index);
    setUpdatedAllergies(newAllergies);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const checkOngoingAllergies = (index) => {
    const newAllergies = updatedAllergies.map((item, i) => {
      if (i === index) {
        return { ...item, ongoing: !item.ongoing };
      }
      return item;
    });
    
    setUpdatedAllergies(newAllergies);
  };
  //END ALLERGY LOGIC

  //START MEDICATION LOGIC
  const openMedicationDrawer = (index) => {
    const isEditing = index < updatedMedications.length; // Check if it's an edit operation

    // Provide a unique key based on index to force re-render
    setOpen(true);
    setDrawerContent(
      <div className="w-full" key={`drawer-${index}`}>
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">MEDICATION</h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px]">
            {isEditing ? "Edit your medication" : "Enter your medication name"}
          </h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            You can update your medication details anytime from your profile page.
          </p>
          <form
            onSubmit={(e) => isEditing ? editMedication(e, index) : addMedication(e, index)}
            className="flex flex-col gap-6"
          >
            <TextInput
              defaultValue={isEditing ? updatedMedications[index]?.medicationName || updatedMedications[index]?.MedicationName || "" : ""}
              label={"Medication Name"}
              name={`medicationName${index}`}
              required={true}
            />

            <TextInput
              defaultValue={isEditing ? updatedMedications[index]?.medicationDosage || updatedMedications[index]?.Dosage || "" : ""}
              label="Dosage"
              name={`dosage${index}`}
              required={true}
            />

            <TextInput
              defaultValue={isEditing ? updatedMedications[index]?.medicationFrequency || updatedMedications[index]?.Frequency || "" : ""}
              label="Frequency"
              name={`frequency${index}`}
              required={true}
            />

            <label className={`flex flex-col gap-1`}>
              <div>
                <span className="text-sportsBlack font-medium text-base leading-[26px] mb-1">Ongoing</span>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  defaultChecked={isEditing ? updatedMedications[index]?.ongoing === 1 || updatedMedications[index]?.OnGoing === 1 ? true : false : false }
                  onChange={checkOngoingMedication}
                  className="checkbox checkbox-medical"
                  name={`ongoing${index}`}
                />
              </div>
            </label>

            <TextInput
              defaultValue={isEditing ? updatedMedications[index]?.additionalInformation || updatedMedications[index]?.Notes || "" : ""}
              name={`additionalInformation${index}`}
              label="Additional Information"
            />

            <Button submit className="!text-[#101820]">
              Update Medication
            </Button>

            {isEditing && (
              <Button
                onClick={() => removeMedication(index)}
                status="nobg"
                className="flex w-full justify-center gap-2 text-sportsRed underline font-semibold"
              >
                <BinIcon />
                Remove
              </Button>
            )}
          </form>
        </div>
      </div>
    );
  };

  const addMedication = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);
    const ongoing = newDetails.get(`ongoing${index}`) || "off";

    const newMedicationData = {
      medicationName: newDetails.get(`medicationName${index}`),
      medicationDosage: newDetails.get(`dosage${index}`),
      medicationFrequency: newDetails.get(`frequency${index}`),
      Notes: newDetails.get(`additionalInformation${index}`),
      dateAdded: new Date(),
      OnGoing: ongoing == "on" ? 1 : 0,
    }

    setUpdatedMedications([...updatedMedications, newMedicationData]);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const editMedication = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);

    const newMedications = updatedMedications.map((item, i) => {
      if (i === index) {
        const ongoing = newDetails.get(`ongoing${index}`) || "off";

        return {
          ...item,
          medicationName: newDetails.get(`medicationName${index}`),
          medicationDosage: newDetails.get(`dosage${index}`),
          medicationFrequency: newDetails.get(`frequency${index}`),
          Notes: newDetails.get(`additionalInformation${index}`),
          OnGoing: ongoing == "on" ? 1 : 0,
        };
      }
      return item;
    });

    setUpdatedMedications(newMedications);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const removeMedication = (index) => {
    const medicationToRemove = updatedMedications[index]; // Get the medication being removed
    
    // Check if the medication has a MedicationId and add it to the removeMedications state
    if (medicationToRemove.MedicationId) {
      setRemoveMedications((prev) => [...prev, medicationToRemove]);
    }
  
    // Remove the medication from updatedMedications
    const newMedications = updatedMedications.filter((_, i) => i !== index);
    setUpdatedMedications(newMedications);
  
    // Close the drawer and reset the content
    setOpen(false);
    setDrawerContent(<div></div>);
  };
  
  const checkOngoingMedication = (index) => {
    const newMedications = updatedMedications.map((item, i) => {
      if (i === index) {
        return { ...item, ongoing: !item.ongoing };
      }
      return item;
    });
    setUpdatedMedications(newMedications);
  };
  //END MEDICATION LOGIC

  //START EMERGENCY CONTACT LOGIC
  const bronzeSubcriptionDrawer = () => {
    setOpen(true);
    setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              UPGRADE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Upgrade subscription</h3>
            <p className=" text-sportsDarkGrey"> Automated emergency incident SMS to ICE contacts is only available on our silver and gold subscription levels.﻿</p>
              <Button variant="primary" onClick={() => { setOpen(false);navigate('/upgrade')}}>Upgrade Subscription</Button>
              <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
          </div>
        </div>
    )
  }

  const openEmergencyContactDrawer = (index) => {
    const isEditing = index < updatedEmergencyContacts?.length; // Check if it's an edit operation

    setOpen(true);
    setDrawerContent(
      <div className="w-full" key={`drawer-${index}`}>
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">EMERGENCY CONTACT</h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col justify-stretch">
          <h3 className="text-[24px]">
            {isEditing ? "Edit your emergency contact details" : "Enter your emergency contact details"}
          </h3>
          <form
            onSubmit={(e) => isEditing ? editEmergencyContact(e, index) : addEmergencyContact(e, index)}
            className="flex flex-col gap-6"
          >
            <TextInput
              defaultValue={isEditing ? updatedEmergencyContacts[index]?.name || updatedEmergencyContacts[index]?.AdditionalICEName || "" : ""}
              label={"Emergency Contact Name"}
              name={`bandAdditionalICEName${index}`}
              required={true}
            />

            <p className="text-sportsGrey">
              We recommend letting your ICE contacts know that you have listed them
              in the Sports Guardian app.
            </p>

            <AdvancedPhoneInput 
              label="Emergency Contact Number"
              inputName={`bandAdditionalICEPhoneNumber${index}`}
              defaultValue={isEditing ? updatedEmergencyContacts[index]?.phoneNumber || updatedEmergencyContacts[index]?.AdditionalICENumber || "+44" : "+44"}
              required={true}
              error={phoneNumberErrors}
              setError={setPhoneNumberErrors}
            />

            <Toggle 
              name={`bandAdditionalICESMS${index}`}
              label="Automatic SMS"
              defaultChecked={isEditing 
                ? editingContactSMS[index]?.checked 
                  ? true 
                  : updatedEmergencyContacts[index]?.sms 
                    ? updatedEmergencyContacts[index]?.sms 
                    : updatedEmergencyContacts[index]?.EmergencySMS ? 1 : 0
                : false
              }
              readOnly={user?.subscription?.Type === 1 ? true : false}
              user={user}
              modal={true}
            />

            <TextInput
              defaultValue={isEditing ? updatedEmergencyContacts[index]?.relation || updatedEmergencyContacts[index]?.AdditionalICERelationship || "" : ""}
              name={`bandAdditionalICERelation${index}`}
              label="Emergency Contact Relationship"
            />

            <Button submit className="!text-[#101820]">
              Update Emergency Contact
            </Button>

            {isEditing && (
              <Button
                onClick={() => removeEmergencyContact(index)}
                status="nobg"
                className="flex w-full justify-center gap-2 text-sportsRed underline font-semibold"
              >
                <BinIcon />
                Remove
              </Button>
            )}
          </form>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if(editingContactSMS.length > 0){
      openEmergencyContactDrawer(editingContactSMS[0].index);
    }
  }, [editingContactSMS])

  const addEmergencyContact = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);
    const phoneErrors = [];

    try {
      const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(newDetails.get(`bandAdditionalICEPhoneNumber${index}`));
      const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);
  
      if (!validPhoneNumber) {
        phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index}`, error: 'This is not a valid phone number.', index: index });
      }
    } catch {
      phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index}`, error: 'This is not a valid phone number.', index: index });
    }

    // If there are any errors, set them in the state
    if (phoneErrors.length > 0) {
      setPhoneNumberErrors(phoneErrors);
      return false;  // Indicating that validation failed
    }

    const newContactData = {
      name: newDetails.get(`bandAdditionalICEName${index}`),
      phoneNumber: newDetails.get(`bandAdditionalICEPhoneNumber${index}`),
      relation: newDetails.get(`bandAdditionalICERelation${index}`),
      sms: newDetails.get(`bandAdditionalICESMS${index}Value`),
    }

    setUpdatedEmergencyContacts([...updatedEmergencyContacts, newContactData]);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const editEmergencyContact = (event, index) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);
    const phoneErrors = [];

    try {
      const validatePhoneNumber = phoneUtil.parseAndKeepRawInput(newDetails.get(`bandAdditionalICEPhoneNumber${index}`));
      const validPhoneNumber = phoneUtil.isValidNumber(validatePhoneNumber);
  
      if (!validPhoneNumber) {
        phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index}`, error: 'This is not a valid phone number.', index: index });
      }
    } catch {
      phoneErrors.push({ name: `bandAdditionalICEPhoneNumber${index}`, error: 'This is not a valid phone number.', index: index });
    }

    // If there are any errors, set them in the state
    if (phoneErrors.length > 0) {
      setPhoneNumberErrors(phoneErrors);
      return false;  // Indicating that validation failed
    }

    const newContacts = updatedEmergencyContacts.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          name: newDetails.get(`bandAdditionalICEName${index}`),
          phoneNumber: newDetails.get(`bandAdditionalICEPhoneNumber${index}`),
          relation: newDetails.get(`bandAdditionalICERelation${index}`),
          sms: newDetails.get(`bandAdditionalICESMS${index}Value`),
        };
      }

      return item;
    });

    setUpdatedEmergencyContacts(newContacts);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const removeEmergencyContact = (index) => {
    const contactToRemove = updatedEmergencyContacts[index]; // Get the contact being removed
  
    // Check if the contact has an ID and add it to the removeEmergencyContacts state
    if (contactToRemove.AdditionalICEContactId) {
      setRemoveEmergencyContacts((prev) => [...prev, contactToRemove]);
    }
  
    const newContacts = updatedEmergencyContacts.filter((_, i) => i !== index);
    setUpdatedEmergencyContacts(newContacts);
    setOpen(false);
    setDrawerContent(<div></div>);
  };

  const emergencyContactLimit = () => {
    setOpen(true);
    setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              UPGRADE
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Upgrade subscription</h3>
            <p className=" text-sportsDarkGrey">Your subscription only allows you to add up to {user?.subscription?.AdditionalIceContacts} additional ICE contacts per band.﻿</p>
            {user?.subscription?.Name !== "Gold" && (
              <Button variant="primary" onClick={() => { setOpen(false);navigate('/upgrade')}}>Upgrade Subscription</Button>
            )}
            
            <Button variant="quaternary" onClick={() => { setOpen(false);}}>Close</Button>
          </div>
        </div>
    )
  }
  //END EMERGENCY CONTACT LOGIC
  const [emergencyDrawerIndex, setEmergencyDrawerIndex] = useState(null);

  useEffect(() => {
    if(phoneNumberErrors?.length > 0){
      const currentIndex = phoneNumberErrors[0]?.index;
      setEmergencyDrawerIndex(currentIndex);
      openEmergencyContactDrawer(currentIndex);
    } else if (emergencyDrawerIndex && phoneNumberErrors !== null ){
      openEmergencyContactDrawer(emergencyDrawerIndex);
      setEmergencyDrawerIndex(null);
    }
  }, [phoneNumberErrors]);

  const editingCoreDetails = (event) => {
    const fieldName = event?.target?.name || event;
  
    if (user?.subscription?.Name === "Lite" || hasAcknowledgedCoreWarning) {
      activateField(fieldName);
      return;
    }
  
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">UPDATE DETAILS</h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Update your core details?</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            If you update any of your core details, these will no longer match the details engraved on your band. We recommend that you order a new band with your updated core details.
          </p>
          <Button className="w-full" href={`/shop/my-participants`} onClick={() => setOpen(false)}>Order New Band</Button>
          <Button variant="quaternary" onClick={() => setOpen(false)}>Don't Change Details</Button>
          <Button
            variant="secondary"
            className="!bg-sportsRed text-white"
            onClick={() => {
              setOpen(false);
              setHasAcknowledgedCoreWarning(true); // 👈 Set the global override
              setTimeout(() => activateField(fieldName), 500); // ⏱ Delay to let drawer finish
            }}
          >
            Continue Anyway
          </Button>
        </div>
      </div>
    );
  };
  
  const activateField = (fieldName) => {
    if (fieldName === "participantName") {
      setParticipantNameWarning(true);
      document.querySelector('input[name="participantName"]')?.focus();
    } else if (fieldName === "bandICEPhoneNumber") {
      setPrimaryICEWarning(true);
      document.querySelector('input[name="bandICEPhoneNumber"]')?.focus();
    } else if (fieldName === "bandPrimaryCondition") {
      setParticipantPrimaryWarning(true);
      document.querySelector('input[name="bandPrimaryCondition"]')?.focus();
    }
  };
  
  const fetchBand = async () => {
    await Sentry.startSpan({ name: "Fetch Band", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Bands/FetchBand?bandid=${band?.BandID}`;
        span.setAttribute("http.url", url);
        span.setAttribute("bandID", band?.BandID || "unknown");
  
        const response = await fetchWithRefresh(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message);});
  
        if (response.ok) {
          const data = await response.json();
          setBandData(data);
          span.setStatus("ok");
        } else {
          span.setStatus("error");
          setRefreshDate(false);
          throw new Error(`Failed to fetch band. Status: ${response.status}`);
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { bandID: band?.BandID } });
        LogErrors(user, logout, updateTokens, "Error fetching band: " + error);
        span.setStatus("error");
      } finally {
        span.end();
      }
    });
  };

  return (
    <div className="w-full flex flex-col justify-stretch mt-[20px] mb-[65px]">
      <form onSubmit={handleBandActivation} className="flex flex-col">
        <div className="space-y-4 flex flex-col justify-stretch p-[24px]">
          <div className="flex items-center gap-4">
            {user?.subscription?.Name === "Lite" ? (
              <div className="relative cursor-pointer" onClick={showLiteDrawer}>
                <div className="pointer-events-none">
                  <AzureProfilePictureUpload activeAccount={updatedParticipant} participant={true} temporaryImage={updatedParticipant?.profilePicture} uploadExternal={true}/>
                </div>
                <div className="absolute inset-0" />
              </div>
            ) : (
              <AzureProfilePictureUpload activeAccount={updatedParticipant} participant={true} temporaryImage={updatedParticipant?.profilePicture} uploadExternal={true}/>
            )}
          </div>

          <div className="border-dashed border-t border-[#E3E3E3]" />

          <div>
            <TextInput
              label="Participant Name"
              name="participantName"
              required={true}
              defaultValue={updatedParticipant?.ParticipantName}
              onClick={(e) => !participantNameWarning && editingCoreDetails(e)}
              readOnly={!participantNameWarning ? true : false}
            />
          </div>

          <div>
            <span className="text-sportsBlack font-medium text-base leading-[26px] mb-3 block">Participant Date of Birth*</span>
            <div className="grid grid-cols-3 gap-[6px]">
              <SelectInput 
                label="Day"
                placeholder="Select"
                name="participantDOBDay"
                defaultValue={selectedDay}
                options={days?.map((day, index) => (<option key={day + '_' + index} value={day.value}>{day.label}</option>))}
                onChange={(e) => setSelectedDay(e.target.value)}
              />
              <SelectInput 
                label="Month"
                placeholder="Select"
                name="participantDOBMonth"
                defaultValue={selectedMonth}
                options={months?.map((month, index) => (<option key={month + '_' + index} value={month.value}>{month.label}</option>))}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <SelectInput 
                label="Year"
                placeholder="Select"
                name="participantDOBYear"
                defaultValue={selectedYear}
                options={years?.map((year, index) => (<option key={year + '_' + index} value={year.value}>{year.label}</option>))}
                onChange={(e) => setSelectedYear(e.target.value)}
              />
            </div>
            <input type="hidden" name="participantDOB" value={participantDOB} />
          </div>

          <div className="border-dashed border-t border-[#E3E3E3]" />

          <div>
            <TextInput
              label="Primary Medical Condition"
              name="bandPrimaryCondition"
              required={true}
              defaultValue={updatedParticipant?.PrimaryMedicalCondition}
              onClick={(e) => !participantPrimaryWarning && editingCoreDetails(e)}
              readOnly={!participantPrimaryWarning ? true : false}
            />
          </div>

          <div className="border-dashed border-t border-[#E3E3E3]" />

          <div>
            <TextInput
              label="Primary Emergency Contact Name (ICE)"
              name="bandICEName"
              defaultValue={updatedParticipant?.PrimaryIceName}
              required={true}
            />
          </div>

          <p className="text-sportsGrey">
            We recommend letting your ICE contacts know that you have listed them
            in the Sports Guardian app.
          </p>

          <AdvancedPhoneInput 
            label="Primary Emergency Contact Number"
            inputName="bandICEPhoneNumber"
            onClick={!primaryICEWarning && ( () => { editingCoreDetails("bandICEPhoneNumber")} )}
            readOnly={!primaryICEWarning ? true : false}
            defaultValue={updatedParticipant?.PrimaryIceNumber || "+44"}
            required={true}
            error={updatingErrors}
            setError={setUpdatingErrors}
          />

          <Toggle
            name="EmergencySMSPrimaryContact"
            label="Automatic SMS"
            defaultChecked={updatedParticipant?.EmergencySMSPrimaryContact && true}
            readOnly={user?.subscription?.Name === "Lite" || user?.subscription?.Type === 1 ? true : false}
            onClick={(e) => {
              if (user?.subscription?.Name === "Lite") {
                e.preventDefault();
                showLiteDrawer();
              } else if (user?.subscription?.Type === 1) {
                e.preventDefault();
                bronzeSubcriptionDrawer();
              }
            }}
          />

          <div>
            <TextInput
              label="Primary Emergency Contact Relationship"
              name="bandICERelation"
              required={true}
              defaultValue={updatedParticipant?.PrimaryIceRelationship}
            />
          </div>

          <div className="border-dashed border-t border-[#E3E3E3]" />
        
          <div>
            <TextArea 
              label="Notes"
              name="participantNotes"
              defaultValue={updatedParticipant?.Notes}
            />
          </div>

          <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-2 !mt-6" />

          {user?.subscription?.Name === "Lite" ? (
            <div className="relative cursor-pointer" onClick={showLiteDrawer}>
              <div className="pointer-events-none">
                {updatedParticipant?.AudioUrl ? (
                  <VoiceNote existingAudio={updatedParticipant?.AudioUrl} subscription={user?.subscription?.Type} editable={true} />
                ) : (
                  <VoiceNote subscription={user?.subscription?.Type} editable={true} />
                )}
              </div>
              <div className="absolute inset-0" />
            </div>
          ) : (
            updatedParticipant?.AudioUrl ? (
              <VoiceNote existingAudio={updatedParticipant?.AudioUrl} subscription={user?.subscription?.Type} editable={true} />
            ) : (
              <VoiceNote subscription={user?.subscription?.Type} editable={true} />
            )
          )}
        </div>

        <div className="bg-sportsLightGrey border-[#E3E3E3] border-t-[1px] p-[24px] flex justify-center flex-col gap-6">
          {user?.subscription?.Name === "Lite" ? (
            <div className="relative cursor-pointer" onClick={showLiteDrawer}>
              <div className="pointer-events-none">
                <DropDown title="Medical History" icon="medical" />
              </div>
              <div className="absolute inset-0" />
            </div>
          ) : (
          <DropDown title="Medical History" icon="medical">
            <div className='flex flex-col gap-y-[15px]'>
              {medicalHistory?.map((condition, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${medicalHistory?.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  {/* Displaying condition details */}
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Condition Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition?.ConditionName}</p>
                      <p className='font-poppins text-[12px] leading-[12px] text-sportsGrey'>Date Added • {formatDate(condition?.DateAdded)}</p>
                    </div>
                    <EditPencilIcon onClick={() => openMedicalDrawer(index)} /> {/* Edit */}
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition?.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <EditPencilIcon onClick={() => openMedicalDrawer(index)} /> 
                  </div>

                  {condition.Notes && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition?.Notes}</p>
                      </div>
                      <EditPencilIcon onClick={() => openMedicalDrawer(index)} /> {/* Edit */}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add button opens the drawer for adding new condition */}
            <div>
              <Button variant="tertiary" className="mx-auto" onClick={() => openMedicalDrawer(medicalHistory?.length)}>
                <AddIcon className="w-4 h-4"/> Add
              </Button>
            </div>
          </DropDown>
          )}

          {user?.subscription?.Name === "Lite" ? (
            <div className="relative cursor-pointer" onClick={showLiteDrawer}>
              <div className="pointer-events-none">
                <DropDown title="Allergies" icon="allergy" />
              </div>
              <div className="absolute inset-0" />
            </div>
          ) : (
          <DropDown title="Allergies" icon="allergy">
            <div className='flex flex-col gap-y-[15px]'>
              {updatedAllergies?.map((allergy, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${updatedAllergies?.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Allergy Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy?.name || allergy?.ConditionName}</p>
                    </div>
                    
                    <EditPencilIcon onClick={() => openAllergyDrawer(index)} />
                  </div>

                  {/* <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy?.ongoing || allergy?.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <EditPencilIcon onClick={() => openAllergyDrawer(index)} /> 
                  </div> */}

                  {(allergy?.additionalInformation || allergy?.Notes) && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy?.additionalInformation || allergy?.Notes}</p>
                      </div>
                      
                      <EditPencilIcon onClick={() => openAllergyDrawer(index)} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Button variant="tertiary" className="mx-auto" onClick={() => openAllergyDrawer(updatedAllergies?.length)}>
                <AddIcon className="w-4 h-4"/> Add
              </Button>
            </div>
          </DropDown>
          )}

          {user?.subscription?.Name === "Lite" ? (
            <div className="relative cursor-pointer" onClick={showLiteDrawer}>
              <div className="pointer-events-none">
                <DropDown title="Medications" icon="medication" />
              </div>
              <div className="absolute inset-0" />
            </div>
          ) : (
          <DropDown title="Medications" icon="medication">
            <div className='flex flex-col gap-y-[15px]'>
              {updatedMedications?.map((medication, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${updatedMedications?.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Medication Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication?.medicationName || medication?.MedicationName}</p>
                    </div>
                    
                    <EditPencilIcon onClick={() => openMedicationDrawer(index)}/>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Dosage</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication?.medicationDosage || medication?.Dosage}</p>
                    </div>
                    
                    <EditPencilIcon onClick={() => openMedicationDrawer(index)}/>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Frequency</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication?.medicationFrequency || medication?.Frequency}</p>
                    </div>

                    <EditPencilIcon onClick={() => openMedicationDrawer(index)}/>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication?.ongoing || medication?.OnGoing ? "Yes" : "No"}</p>
                    </div>
                    <EditPencilIcon onClick={() => openMedicationDrawer(index)} /> {/* Edit */}
                  </div>

                  {(medication?.additionalInformation || medication?.Notes) && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication?.additionalInformation || medication?.Notes }</p>
                      </div>
                      
                      <EditPencilIcon onClick={() => openMedicationDrawer(index)}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Button variant="tertiary" className="mx-auto" onClick={() => openMedicationDrawer(updatedMedications?.length)}>
                <AddIcon className="w-4 h-4"/> Add
              </Button>
            </div>
          </DropDown>
          )}

          {user?.subscription?.Name === "Lite" ? (
            <div className="relative cursor-pointer" onClick={showLiteDrawer}>
              <div className="pointer-events-none">
                <DropDown title="Other Emergency Contacts" icon="contact" />
              </div>
              <div className="absolute inset-0" />
            </div>
          ) : (
          <DropDown title="Other Emergency Contacts" icon="contact">
            <div className='flex flex-col gap-y-[15px]'>
              {updatedEmergencyContacts?.map((contact, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${updatedEmergencyContacts?.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Emergency Contact {index + 1}</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.name || contact?.AdditionalICEName} - {contact?.phoneNumber || contact?.AdditionalICENumber}</p>
                    </div>
                    <EditPencilIcon onClick={() => openEmergencyContactDrawer(index)}/>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Relationship</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.relation || contact?.AdditionalICERelationship}</p>
                    </div>
                    <EditPencilIcon onClick={() => openEmergencyContactDrawer(index)}/>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Automated SMS</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>
                        {contact.sms ? contact.sms === "1" || contact.sms === "on" ? "Enabled" : "Disabled" : contact?.EmergencySMS ? "Enabled" : "Disabled" }
                      </p>
                    </div>
                    <EditPencilIcon onClick={() => openEmergencyContactDrawer(index)}/>
                  </div>
                </div>
              ))}
              <div>
                <Button 
                  className="mx-auto" 
                  variant="tertiary" 
                  onClick={() => {
                    updatedEmergencyContacts.length === user?.subscription?.AdditionalIceContacts 
                      ? emergencyContactLimit()
                      : openEmergencyContactDrawer(updatedEmergencyContacts?.length)
                  }}
                >
                  <AddIcon className="w-4 h-4"/> Add
                </Button>
              </div>
            </div>
          </DropDown>
          )}

          <div className="font-semibold flex gap-5 pr-[20px]">
            <CheckBox 
              name="confirmation" 
              onInput={(e) => e.target.setCustomValidity("")}
              onInvalid={(e) => e.target.setCustomValidity('Our partners rely on this information to assist you in the event of an emergency. Please confirm your details are complete and up to date.')}
              required
            /> 
            I confirm all the information provided is correct and up to date
          </div>

          <Button submit={true} disabled={updatingProfile && true}>
            {updatingProfile ? "Updating " : "Update "}Details
          </Button>
        </div>
      </form>
    </div>
  )
}